import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import Avatar from "@material-ui/core/Avatar";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import AnnouncementIcon from "@material-ui/icons/Notifications";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import RemoveIcon from "@material-ui/icons/Remove";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import AnnouncementModal from "../../components/AnnouncementModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Grid } from "@material-ui/core";
import { isArray } from "lodash";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import AttachmentPreview from "../../components/AttachmentPreview";

const reducer = (state, action) => {
  if (action.type === "LOAD_ANNOUNCEMENTS") {
    const announcements = action.payload;
    const newAnnouncements = [];

    if (isArray(announcements)) {
      announcements.forEach((announcement) => {
        const announcementIndex = state.findIndex(
          (u) => u.id === announcement.id
        );
        if (announcementIndex !== -1) {
          state[announcementIndex] = announcement;
        } else {
          newAnnouncements.push(announcement);
        }
      });
    }

    return [...state, ...newAnnouncements];
  }

  if (action.type === "UPDATE_ANNOUNCEMENTS") {
    const announcement = action.payload;
    const announcementIndex = state.findIndex((u) => u.id === announcement.id);

    if (announcementIndex !== -1) {
      state[announcementIndex] = announcement;
      return [...state];
    } else {
      return [announcement, ...state];
    }
  }

  if (action.type === "DELETE_ANNOUNCEMENT") {
    const announcementId = action.payload;

    const announcementIndex = state.findIndex((u) => u.id === announcementId);
    if (announcementIndex !== -1) {
      state.splice(announcementIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  // ✅ CORREGIDO: MainPaper dinámico según tema
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
  },
  // ✅ CORREGIDO: WelcomeCard dinámico según tema (mantiene gradiente)
  welcomeCard: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    marginBottom: theme.spacing(3),
    borderRadius: 12,
    padding: theme.spacing(3),
    boxShadow: theme.mode === 'dark' ? '0 4px 20px rgba(102, 126, 234, 0.3)' : '0 4px 20px rgba(0,0,0,0.1)',
  },
  welcomeIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(2),
  },
  newButton: {
    borderRadius: 6,
    textTransform: 'none',
    fontWeight: 500,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    }
  },
  // ✅ CORREGIDO: AnnouncementCard dinámico según tema
  announcementCard: {
    marginBottom: theme.spacing(2),
    borderRadius: 8,
    transition: 'all 0.3s ease',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
    boxShadow: theme.mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
    '&:hover': {
      boxShadow: theme.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.1)',
      transform: 'translateY(-2px)',
    }
  },
  announcementCardSelected: {
    border: `2px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.primary.light + '10',
  },
  announcementAvatar: {
    backgroundColor: theme.palette.primary.main,
    width: 48,
    height: 48,
  },
  announcementTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  announcementSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  priorityChip: {
    fontWeight: 600,
    borderRadius: 16,
  },
  statusChip: {
    fontWeight: 600,
    borderRadius: 16,
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  actionButton: {
    borderRadius: 8,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.1)',
    }
  },
  editButton: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.info.main,
    }
  },
  deleteButton: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.main,
    }
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary || theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
  },
  // ✅ CORREGIDO: TextField dinámico según tema
  textField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.mode === 'dark' ? '#374151' : '#f5f5f5',
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#eeeeee',
      },
      '&.Mui-focused': {
        backgroundColor: theme.mode === 'dark' ? '#374151' : 'white',
        boxShadow: `0 0 0 2px ${theme.mode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(25, 118, 210, 0.2)'}`,
      },
      "& fieldset": {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
  emptyIcon: {
    fontSize: 64,
    marginBottom: theme.spacing(2),
    opacity: 0.5,
  },
}));

const Announcements = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [deletingAnnouncement, setDeletingAnnouncement] = useState(null);
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [announcements, dispatch] = useReducer(reducer, []);

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    if (!user || user.id == null) return;
    const canAccess =
      user.super === true ||
      user.super === 1 ||
      String(user.super) === "1" ||
      user.profile === "admin";
    if (!canAccess) {
      toast.error("No tienes permiso para acceder a esta página. Te redirigimos al inicio.");
      setTimeout(() => {
        history.push(`/`);
      }, 1000);
    }
  }, [user, history]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchAnnouncements();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-announcement`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_ANNOUNCEMENTS", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_ANNOUNCEMENT", payload: +data.id });
      }
    });

    socket.on(`company-announcement`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_ANNOUNCEMENTS", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_ANNOUNCEMENT", payload: +data.id });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [socketManager, user.companyId]);

  const fetchAnnouncements = async () => {
    try {
      const { data } = await api.get("/announcements/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_ANNOUNCEMENTS", payload: data.records });
      setHasMore(Boolean(data.hasMore));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAnnouncementModal = () => {
    setSelectedAnnouncement(null);
    setAnnouncementModalOpen(true);
  };

  const handleCloseAnnouncementModal = () => {
    setSelectedAnnouncement(null);
    setAnnouncementModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditAnnouncement = (announcement) => {
    setSelectedAnnouncement(announcement);
    setAnnouncementModalOpen(true);
  };

  const handleDeleteAnnouncement = async (announcement) => {
    if (!announcement?.id) return;
    try {
      if (announcement.mediaName) {
        await api.delete(`/announcements/${announcement.id}/media-upload`);
      }

      await api.delete(`/announcements/${announcement.id}`);
      dispatch({ type: "DELETE_ANNOUNCEMENT", payload: announcement.id });
      toast.success(i18n.t("announcements.toasts.deleted"));
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingAnnouncement(null);
      setConfirmModalOpen(false);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const translatePriority = (val) => {
    if (val === 1) {
      return "Alta";
    }
    if (val === 2) {
      return "Media";
    }
    if (val === 3) {
      return "Baja";
    }
  };

  const getPriorityIcon = (val) => {
    if (val === 1) {
      return <KeyboardArrowUpIcon style={{ color: '#f44336' }} />;
    }
    if (val === 2) {
      return <RemoveIcon style={{ color: '#ff9800' }} />;
    }
    if (val === 3) {
      return <KeyboardArrowDownIcon style={{ color: '#4caf50' }} />;
    }
  };

  const getPriorityColor = (val) => {
    if (val === 1) {
      return '#f44336';
    }
    if (val === 2) {
      return '#ff9800';
    }
    if (val === 3) {
      return '#4caf50';
    }
  };

  const getStatusIcon = (status) => {
    return status ? 
      <CheckCircleIcon style={{ color: '#4caf50' }} /> : 
      <CancelIcon style={{ color: '#f44336' }} />;
  };

  const getStatusColor = (status) => {
    return status ? '#4caf50' : '#f44336';
  };

  const getStatusText = (status) => {
    return status ? 'Activo' : 'Inactivo';
  };


  const renderAnnouncementCard = (announcement) => {
    return (
      <Card
        key={announcement.id}
        className={classes.announcementCard}
        style={{ cursor: 'pointer' }}
        onClick={() => handleEditAnnouncement(announcement)}
      >
        <CardContent style={{ padding: 16, backgroundColor: theme.palette.background.paper }}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between">
            <Box display="flex" alignItems="flex-start" flex={1}>
              <Avatar className={classes.announcementAvatar}>
                <AnnouncementIcon />
              </Avatar>
              
              <Box flex={1} style={{ marginLeft: 16 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="h6" className={classes.announcementTitle} style={{ color: theme.palette.text.primary }}>
                    {announcement.title}
                  </Typography>
                  
                  <Box display="flex" gap={1}>
                    <Chip
                      size="small"
                      icon={getPriorityIcon(announcement.priority)}
                      label={translatePriority(announcement.priority)}
                      className={classes.priorityChip}
                      style={{ backgroundColor: getPriorityColor(announcement.priority) + '20', color: getPriorityColor(announcement.priority) }}
                    />
                    <Chip
                      size="small"
                      icon={getStatusIcon(announcement.status)}
                      label={getStatusText(announcement.status)}
                      className={classes.statusChip}
                      style={{ backgroundColor: getStatusColor(announcement.status) + '20', color: getStatusColor(announcement.status) }}
                    />
                  </Box>
                </Box>
                
                <Typography variant="body2" className={classes.announcementSubtitle} style={{ color: theme.palette.text.secondary || theme.palette.text.primary }} paragraph>
                  {String(announcement.text || "").length > 100
                    ? `${String(announcement.text).substring(0, 100)}...`
                    : (announcement.text || "Sin contenido")}
                </Typography>
                
                {(announcement.mediaPath || announcement.mediaName) && (
                  <Box mt={1} onClick={(e) => e.stopPropagation()}>
                    <AttachmentPreview
                      size="compact"
                      mediaPath={announcement.mediaPath || announcement.mediaName}
                      mediaName={announcement.mediaName}
                    />
                  </Box>
                )}
              </Box>
            </Box>
            
            <Box className={classes.actionButtons}>
              <Tooltip title="Editar anuncio">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditAnnouncement(announcement);
                  }}
                  className={`${classes.actionButton} ${classes.editButton}`}
                  size="small"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Eliminar anuncio">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmModalOpen(true);
                    setDeletingAnnouncement(announcement);
                  }}
                  className={`${classes.actionButton} ${classes.deleteButton}`}
                  size="small"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderEmptyState = () => {
    if (announcements.length > 0) return null;
    
    return (
      <Box className={classes.emptyState} style={{ backgroundColor: theme.palette.background.paper }}>
        <span className={classes.emptyIcon} role="img" aria-label="announcement">📢</span>
        <Typography variant="h6" style={{ color: theme.palette.text.primary }} gutterBottom>
          No hay anuncios creados
        </Typography>
        <Typography variant="body2" style={{ color: theme.palette.text.secondary || theme.palette.text.primary }} paragraph>
          Comienza creando tu primer anuncio para comunicarte con tu equipo
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenAnnouncementModal}
          className={classes.newButton}
          size="large"
        >
          Crear Primer Anuncio
        </Button>
      </Box>
    );
  };

  return (
    <MainContainer >
      <ConfirmationModal
        title="Eliminar Anuncio"
        open={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setDeletingAnnouncement(null);
        }}
        onConfirm={() => handleDeleteAnnouncement(deletingAnnouncement)}
        confirmDisabled={!deletingAnnouncement}
      >
        <Box>
          <Typography variant="h6" gutterBottom color="error">
            <span role="img" aria-label="warning">⚠️</span> ¿Estás seguro de que quieres eliminar este anuncio?
          </Typography>
          <Typography variant="body1" paragraph>
            Esta acción <strong>no se puede deshacer</strong> y eliminará permanentemente:
          </Typography>
          <Box component="ul" pl={2}>
            <Typography component="li" variant="body2">
              El anuncio y todo su contenido
            </Typography>
            <Typography component="li" variant="body2">
              El archivo adjunto (si existe)
            </Typography>
            <Typography component="li" variant="body2">
              El historial de visualizaciones
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
            <strong>Anuncio:</strong> {deletingAnnouncement?.title || 'Sin título'}
          </Typography>
        </Box>
      </ConfirmationModal>
      <AnnouncementModal
        reload={() => {
          setPageNumber(1);
          fetchAnnouncements();
        }}
        resetPagination={() => {
          setPageNumber(1);
          fetchAnnouncements();
        }}
        open={announcementModalOpen}
        onClose={handleCloseAnnouncementModal}
        aria-labelledby="form-dialog-title"
        announcementId={selectedAnnouncement && selectedAnnouncement.id}
      />
      <MainHeader>
        <Grid style={{ width: "99.6%" }} container>
          <Grid xs={12} sm={8} item>
            <Title>
              {i18n.t("announcements.title")} ({announcements.length})
            </Title>
          </Grid>
          <Grid xs={12} sm={4} item>
            <Grid spacing={2} container>
              <Grid xs={6} sm={6} item>
                <TextField
                  fullWidth
                  placeholder={i18n.t("announcements.searchPlaceholder")}
                  type="search"
                  value={searchParam}
                  onChange={handleSearch}
                  className={classes.textField}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: theme.palette.text.secondary || theme.palette.text.primary }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid xs={6} sm={6} item>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleOpenAnnouncementModal}
                  color="primary"
                  startIcon={<AddIcon />}
                  className={classes.newButton}
                  size="medium"
                >
                  NUEVO ANUNCIO
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
        style={{ backgroundColor: theme.palette.background.paper }}
      >
        {loading && announcements.length === 0 ? (
          <Box display="flex" justifyContent="center" p={3}>
            <Typography variant="body2" style={{ color: theme.palette.text.secondary || theme.palette.text.primary }}>
              Cargando anuncios...
            </Typography>
          </Box>
        ) : announcements.length > 0 ? (
          <Box>
            {announcements.map((announcement) => renderAnnouncementCard(announcement))}
            {loading && (
              <Box display="flex" justifyContent="center" p={3}>
                <Typography variant="body2" style={{ color: theme.palette.text.secondary || theme.palette.text.primary }}>
                  Cargando más anuncios...
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          renderEmptyState()
        )}
      </Paper>
    </MainContainer >
  )
};

export default Announcements;